/**
 * «Видео» — камеры салонов из Macroscop: `GET /api/video/cities|cameras|frame|fragments`,
 * события сделки `GET /api/deals/{id}/video`, ролик `POST /api/video/clip` и отправка в чат.
 * Время везде — МСК без пояса, `ГГГГ-ММ-ДДTЧЧ:ММ:СС`. Спека `docs/specs/2026-10-23-video-design.md`.
 */
import { apiFetch, query } from "@/lib/api";

export type VideoCity = { id: number; name: string; cameras: number };
export type VideoCamera = { id: number; name: string; location_id: number; grp: string | null; status: string | null };
export type Fragment = { from: string; to: string };

export type DealVideoEvent = {
  kind: "visit_start" | "visit_end" | "contract" | "payment" | "give" | "return" | "client_in" | "client_out" | "nonreturn" | "move";
  title: string;
  detail: string | null;
  user: string | null;
  at: string;
  from: string;
  to: string;
  location_id: number | null;
  location: string | null;
  cameras: { id: number; name: string }[];
};

export type Clip = { key: string; size: number; seconds: number; camera: string };
/** Строка истории роликов (`GET /api/video/clips`): `gone` — собирался до рестарта сервиса. */
export type ClipRow = {
  key: string;
  camera_id: number;
  camera: string;
  from: string;
  to: string;
  deal_id: number | null;
  chat_id: number | null;
  user: string;
  created: string | null;
  status: "ready" | "running" | "error" | "gone";
  size?: number;
  error?: string;
};
type ClipState = Omit<Clip, "size"> & { ready: boolean; size?: number; error?: string };

export const videoCitiesQuery = () => ({
  queryKey: ["video-cities"],
  queryFn: () => apiFetch<VideoCity[]>("/api/video/cities"),
  staleTime: 600_000,
});

export const videoCamerasQuery = (location: number | null) => ({
  queryKey: ["video-cameras", location],
  queryFn: () => apiFetch<VideoCamera[]>(`/api/video/cameras${query({ location })}`),
  enabled: location != null,
  staleTime: 600_000,
});

export const videoFragmentsQuery = (camera: number, day: string) => ({
  queryKey: ["video-fragments", camera, day],
  queryFn: () => apiFetch<{ fragments: Fragment[] }>(`/api/video/fragments${query({ camera, day })}`),
  staleTime: 60_000,
});

export const dealVideoQuery = (dealId: number, enabled = true) => ({
  queryKey: ["deal-video", dealId],
  queryFn: () => apiFetch<{ location_id: number | null; events: DealVideoEvent[] }>(`/api/deals/${dealId}/video`),
  enabled,
  staleTime: 60_000,
});

/** Ролики сделки или салона за день; пока что-то собирается — обновляем раз в 5 с. */
export const videoClipsQuery = (p: { deal?: number | null; location?: number | null; day?: string }) => ({
  queryKey: ["video-clips", p.deal ?? null, p.deal ? null : (p.location ?? null), p.deal ? null : (p.day ?? null)],
  queryFn: () => apiFetch<ClipRow[]>(`/api/video/clips${query(p.deal ? { deal: p.deal } : { location: p.location, day: p.day })}`),
  enabled: p.deal != null || p.location != null,
  refetchInterval: (q: { state: { data?: ClipRow[] | undefined } }) => (q.state.data?.some((r) => r.status === "running") ? 5000 : false),
});

export const frameUrl = (camera: number, at: string, w = 640, base = "") => `${base}/api/video/frame${query({ camera, at, w })}`;
/**
 * Кадры — через очередь: шлюз пускает к prokat 5 запросов в секунду с всплеском 30 на адрес, а шаг по
 * сетке из 20 камер — это 30 кадров разом. Не больше 4 запросов одновременно, большой кадр — в начало
 * очереди, на 429 — повтор с паузой 0,5 → 1 → 2 → 4 с. Отменённый `signal` снимает кадр из очереди.
 */
type FrameJob = { run: () => void; fail: (e: unknown) => void };
const frameQueue: FrameJob[] = [];
let frameBusy = 0;
const FRAME_LIMIT = 4;
const pumpFrames = () => {
  while (frameBusy < FRAME_LIMIT && frameQueue.length) {
    frameBusy++;
    frameQueue.shift()!.run();
  }
};
const aborted = () => new DOMException("кадр больше не нужен", "AbortError");

export function loadFrame(url: string, signal: AbortSignal, first = false): Promise<Blob> {
  return new Promise((ok, fail) => {
    const job: FrameJob = {
      fail,
      run: async () => {
        try {
          for (let i = 0; ; i++) {
            if (signal.aborted) throw aborted();
            const r = await fetch(url, { signal });
            if (r.status === 429 && i < 4) {
              await new Promise((w) => setTimeout(w, 500 * 2 ** i));
              continue;
            }
            if (!r.ok) throw Object.assign(new Error(String(r.status)), { status: r.status });
            ok(await r.blob());
            return;
          }
        } catch (e) {
          fail(e);
        } finally {
          frameBusy--;
          pumpFrames();
        }
      },
    };
    if (first) frameQueue.unshift(job);
    else frameQueue.push(job);
    signal.addEventListener(
      "abort",
      () => {
        const i = frameQueue.indexOf(job);
        if (i >= 0) (frameQueue.splice(i, 1), fail(aborted()));
      },
      { once: true },
    );
    pumpFrames();
  });
}

export const clipUrl =(key: string, download = false, base = "") => `${base}/api/video/clip/${key}${query({ download })}`;

/**
 * Ролик собирается на сервере фоном (6 минут видео — около 70 с, а шлюз рвёт запрос на 60 с):
 * повторяем тот же запрос раз в 3 с, пока сервер не скажет «готов» или не вернёт ошибку.
 */
export async function makeClip(
  camera: number,
  from: string,
  to: string,
  signal?: AbortSignal,
  at: { deal_id?: number | null; chat_id?: number | null } = {},
): Promise<Clip> {
  const until = Date.now() + 15 * 60_000;
  // Первый запрос — строка истории (сделка, чат) и перезапуск упавшего раньше отрезка.
  let start = true;
  while (Date.now() < until) {
    if (signal?.aborted) throw new Error("Окно закрыли");
    const body = start ? { camera, from, to, start, ...at } : { camera, from, to };
    start = false;
    const r = await apiFetch<ClipState>("/api/video/clip", { method: "POST", body: JSON.stringify(body) });
    if (r.error) throw new Error(r.error);
    if (r.ready) return { key: r.key, seconds: r.seconds, camera: r.camera, size: r.size ?? 0 };
    await new Promise((ok) => setTimeout(ok, 3000));
  }
  throw new Error("Ролик не собрался за 15 минут");
}

export const sendClip = (key: string, chatId: number, caption: string, dealId: number | null) =>
  apiFetch<{ sent: boolean; warning?: string }>(`/api/video/clip/${key}/send`, {
    method: "POST",
    body: JSON.stringify({ chat_id: chatId, caption, deal_id: dealId }),
  });

/** Время МСК как строка `ГГГГ-ММ-ДДTЧЧ:ММ:СС` ↔ секунды от начала суток (без поясов браузера). */
const pad = (n: number) => String(n).padStart(2, "0");
export const dayOf = (at: string) => at.slice(0, 10);
export const secOf = (at: string) => Number(at.slice(11, 13)) * 3600 + Number(at.slice(14, 16)) * 60 + Number(at.slice(17, 19) || 0);
export const atOf = (day: string, sec: number) => {
  const s = Math.max(0, Math.min(86399, Math.round(sec)));
  return `${day}T${pad(Math.floor(s / 3600))}:${pad(Math.floor(s / 60) % 60)}:${pad(s % 60)}`;
};
export const hms = (at: string) => at.slice(11, 19);
/** Секунды от полуночи → «ЧЧ:ММ:СС». */
export const clock = (sec: number) => hms(atOf("0000-00-00", sec));
export const shortDay = (at: string) => `${at.slice(8, 10)}.${at.slice(5, 7)}`;
/** Длительность: «45 с», «1 мин 50 с». */
export const dur = (sec: number) => (sec < 60 ? `${sec} с` : `${Math.floor(sec / 60)} мин${sec % 60 ? ` ${sec % 60} с` : ""}`);

/** Сегодня по Москве — день по умолчанию на экране «Видео». */
export function todayMsk(): string {
  const d = new Date(Date.now() + 3 * 3600_000);
  return d.toISOString().slice(0, 10);
}
